/**
 * Full screen for one element.
 *
 * Two things make this more than a one-liner:
 *
 *  - The browser can leave full screen without us — Esc, F11, switching tabs.
 *    So the state is driven by the `fullscreenchange` event, never by the click
 *    handler, or the button ends up lying about which mode you are in.
 *
 *  - `requestFullscreen` is not always granted. This app also runs as a
 *    micro-frontend, and an iframe without `allow="fullscreen"` rejects the
 *    request. Rather than a button that silently does nothing, we fall back to
 *    covering the window with CSS, which gets the same result inside the frame.
 */
import { useCallback, useEffect, useState, type RefObject } from 'react';

export type FullscreenMode = 'off' | 'native' | 'fallback';

interface VendorElement extends HTMLElement {
  webkitRequestFullscreen?: () => Promise<void>;
}
interface VendorDocument extends Document {
  webkitFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => Promise<void>;
}

export const useFullscreen = <T extends HTMLElement>(ref: RefObject<T | null>) => {
  const [mode, setMode] = useState<FullscreenMode>('off');

  useEffect(() => {
    const doc = document as VendorDocument;
    const sync = () => {
      const el = doc.fullscreenElement ?? doc.webkitFullscreenElement ?? null;
      if (el && el === ref.current) setMode('native');
      // Only clear a mode the browser owns — leaving the CSS fallback is ours.
      else setMode((m) => (m === 'native' ? 'off' : m));
    };
    document.addEventListener('fullscreenchange', sync);
    document.addEventListener('webkitfullscreenchange', sync);
    return () => {
      document.removeEventListener('fullscreenchange', sync);
      document.removeEventListener('webkitfullscreenchange', sync);
    };
  }, [ref]);

  /*
   * Escape is NOT bound here.
   *
   * Native full screen gets Escape from the browser, and the fallback needs it
   * supplied — but a listener here would be a second one on `document`, racing
   * whatever the screen has bound for its own dismissals. It did exactly that:
   * with a unit selected inside fallback full screen, one keypress closed the
   * details popup AND dropped out of full screen, because both listeners fired.
   *
   * So the caller owns Escape and decides the order, and `exit` is what it
   * calls when full screen is the thing that should give way.
   */
  const exit = useCallback(() => setMode('off'), []);

  const toggle = useCallback(() => {
    const doc = document as VendorDocument;
    if (mode === 'native') {
      (doc.exitFullscreen ?? doc.webkitExitFullscreen)?.call(doc);
      return;
    }
    if (mode === 'fallback') {
      setMode('off');
      return;
    }
    const el = ref.current as VendorElement | null;
    if (!el) return;
    const request = el.requestFullscreen ?? el.webkitRequestFullscreen;
    if (!request) {
      setMode('fallback');
      return;
    }
    // A rejected promise means the frame or the user said no — cover the window
    // ourselves rather than leaving the button apparently broken.
    Promise.resolve(request.call(el)).catch(() => setMode('fallback'));
  }, [mode, ref]);

  return { mode, active: mode !== 'off', toggle, exit };
};
